import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { Savings as ValuationIcon } from '@mui/icons-material';
import { useVenue } from '../../../../context/VenueContext';
import CostTrackingHelpModal from '../../../../components/venue/CostTrackingHelpModal';
import inventoryValuationService from '../../../../services/inventoryValuationService';
import venueLocationService from '../../../../services/venueLocationService';
import type { InventoryValuation as InventoryValuationType, VenueLocation } from '../../../../types/venue';
import { formatQty } from '../../../../utils/quantityFormat';
import { fromMinorUnits } from '../../../../utils/money';
import PageHeader from '../../../../components/venue/PageHeader';
import FilterBar from '../../../../components/venue/FilterBar';
import EmptyState from '../../../../components/venue/EmptyState';

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Format a numeric value using currency as a prefix string (e.g. "EUR", "GBP").
 * Matches the pattern used across other inventory pages (item list fmtCost).
 * Falls back to plain 2dp number when currency is absent.
 */
function fmtValue(value: number, currency: string | null): string {
  const formatted = value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return currency ? `${currency} ${formatted}` : formatted;
}

/** Compact datetime: "Apr 11, 2026 · 14:30" */
function fmtAsOf(iso: string): string {
  const d = new Date(iso);
  const date = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  const time = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  return `${date} · ${time}`;
}

// ── Component ─────────────────────────────────────────────────────────────────

const InventoryValuation: React.FC = () => {
  const theme = useTheme();
  const { venueId } = useVenue();

  const [helpOpen, setHelpOpen] = useState(false);
  const [locationFilter, setLocationFilter] = useState('');
  const [locations, setLocations] = useState<VenueLocation[]>([]);

  const [data, setData] = useState<InventoryValuationType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ── Load locations once ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!venueId) return;
    venueLocationService.getLocations(venueId)
      .then(setLocations)
      .catch(() => { /* non-blocking */ });
  }, [venueId]);

  // ── Load valuation ────────────────────────────────────────────────────────────
  const loadValuation = useCallback(async () => {
    if (!venueId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await inventoryValuationService.getValuation(venueId, {
        locationId: locationFilter || undefined,
      });
      setData(result);
    } catch {
      setError('Failed to load stock estimate. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [venueId, locationFilter]);

  useEffect(() => { loadValuation(); }, [loadValuation]);

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {/* COST-GUIDE.1 — the help affordance opens the shared cost guide. */}
      <PageHeader
        icon={<ValuationIcon />}
        title="Estimated Stock Value"
        onHelp={() => setHelpOpen(true)}
        helpTitle="How cost tracking works"
      />
      <CostTrackingHelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />

      {/* ── Error ────────────────────────────────────────────────────── */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
      )}

      {/* ── Summary card ─────────────────────────────────────────────── */}
      <Paper
        sx={{
          p: 3,
          borderRadius: 2,
          boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
        }}
      >
        <Typography
          variant="caption"
          fontWeight={600}
          sx={{
            color: theme.palette.text.secondary,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          {/* COSTING.1d — recorded vs indicative label. */}
          {data?.basis === 'RECORDED_FIFO' ? 'At recorded cost · FIFO' : 'Indicative only'}
        </Typography>

        {loading ? (
          <Box sx={{ py: 1 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            <Typography variant="h3" fontWeight={700} sx={{ letterSpacing: '-0.02em' }}>
              {data ? fmtValue(fromMinorUnits(data.totalValue), data.currency) : '—'}
            </Typography>
            {data && (
              <Typography variant="body2" color="text.secondary">
                {data.itemCount} item{data.itemCount !== 1 ? 's' : ''} valued
                {data.unvaluedCount > 0 && (
                  <> · <span style={{ color: theme.palette.warning.main }}>{data.unvaluedCount} without cost history</span></>
                )}
                {/* COSTING.1d — uncosted-excluded-and-said (recorded-FIFO mode). */}
                {data.basis === 'RECORDED_FIFO' && (data.uncostedUnits ?? 0) > 0 && (
                  <> · <span style={{ color: theme.palette.warning.main }}>{data.uncostedUnits} unit{data.uncostedUnits === 1 ? '' : 's'} uncosted (excluded from the total)</span></>
                )}
                {data.asOf && (
                  <> · as of {fmtAsOf(data.asOf)}</>
                )}
              </Typography>
            )}
          </>
        )}
      </Paper>

      {/* ── Disclaimer ───────────────────────────────────────────────── */}
      {data && (
        <Alert severity="info" icon={false} sx={{ fontSize: '0.8rem', py: 0.75 }}>
          {data.disclaimer}
        </Alert>
      )}

      {/* ── Location filter ──────────────────────────────────────────── */}
      {locations.length > 0 && (
        <FilterBar>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Location</InputLabel>
            <Select
              value={locationFilter}
              label="Location"
              onChange={e => setLocationFilter(e.target.value)}
            >
              <MenuItem value="">All locations</MenuItem>
              {locations.map(loc => (
                <MenuItem key={loc._id} value={loc._id}>{loc.displayName}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </FilterBar>
      )}

      {/* ── Breakdown table ──────────────────────────────────────────── */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.06)' }}>
        {loading ? (
          <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={28} />
          </Box>
        ) : !data || data.items.length === 0 ? (
          <EmptyState
            icon={ValuationIcon}
            message={data && data.itemCount === 0 && data.unvaluedCount > 0
              ? `${data.unvaluedCount} item${data.unvaluedCount !== 1 ? 's' : ''} in stock but none have a cost recorded yet.`
              : 'No items with positive stock and cost history found.'}
          />
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Wine', 'Location', 'Qty', 'Last Cost', 'Line Value'].map(h => (
                    <TableCell key={h}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.items.map((item, index) => (
                  <TableRow key={`${item.inventoryItemId}-${index}`} hover>
                    {/* Wine */}
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {item.displayName ?? '—'}
                      </Typography>
                    </TableCell>
                    {/* Location */}
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {item.locationName ?? '—'}
                      </Typography>
                    </TableCell>
                    {/* Qty */}
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {formatQty(item.quantity)}
                      </Typography>
                    </TableCell>
                    {/* Last Cost */}
                    <TableCell>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontFamily: 'monospace' }}
                      >
                        {fmtValue(fromMinorUnits(item.lastCost), data.currency)}
                      </Typography>
                    </TableCell>
                    {/* Line Value */}
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{ fontFamily: 'monospace' }}
                      >
                        {fmtValue(fromMinorUnits(item.lineValue), data.currency)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

    </Box>
  );
};

export default InventoryValuation;
